
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Bug, Check } from "lucide-react";

export function BillComponentDebugger() {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testResponse, setTestResponse] = useState<any>(null);
  const { toast } = useToast();

  const handleTestConfig = async () => {
    setTestLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('debug-bill-component', {
        body: { testMode: true }
      });

      if (error) {
        console.error('Config test error:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to test configuration",
        });
        return;
      }
      
      setTestResponse(data);
      
      toast({
        title: "Success",
        description: "Configuration test completed",
      });
    } catch (error: any) {
      console.error('Config test error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to test configuration",
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleDirectTest = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('debug-bill-component', {
        body: { testMode: false }
      });

      if (error) {
        console.error('API test error:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to make API call",
        });
        return;
      }
      
      setResponse(data);
      
      if (data.success) {
        toast({
          title: "Success",
          description: "Bill component created successfully",
        });
      } else {
        toast({
          variant: "destructive",
          title: "API Error",
          description: "API call did not return success. Check the response details.",
        });
      }
    } catch (error: any) {
      console.error('API test error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to make API call",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Bill Component API Debugger</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex space-x-4 mb-4">
            <Button 
              onClick={handleTestConfig}
              disabled={testLoading}
              variant="outline"
            >
              {testLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bug className="mr-2 h-4 w-4" />}
              Test Configuration
            </Button>

            <Button 
              onClick={handleDirectTest}
              disabled={loading}
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Test Direct API Call
            </Button>
          </div>

          <Tabs defaultValue="config" className="mt-4">
            <TabsList>
              <TabsTrigger value="config">Configuration Test</TabsTrigger>
              <TabsTrigger value="api">API Response</TabsTrigger>
            </TabsList>
            <TabsContent value="config" className="pt-4">
              {testResponse && (
                <div>
                  <h3 className="font-semibold mb-2">Configuration:</h3>
                  <pre className="bg-slate-50 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                    {JSON.stringify(testResponse, null, 2)}
                  </pre>
                </div>
              )}
              {!testResponse && <p className="text-gray-500">Run configuration test to see details.</p>}
            </TabsContent>
            <TabsContent value="api" className="pt-4">
              {response && (
                <div>
                  <h3 className="font-semibold mb-2">API Response:</h3>
                  <pre className="bg-slate-50 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                    {JSON.stringify(response, null, 2)}
                  </pre>
                </div>
              )}
              {!response && <p className="text-gray-500">Run direct API test to see the response.</p>}
            </TabsContent>
          </Tabs>
        </div>
      </CardContent>
    </Card>
  );
}
